import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Btn, Field, Input, Select, Modal, Badge } from "@/components/UI";
import { UploadSimple, Sparkle, CheckCircle, Warning, Robot } from "@phosphor-icons/react";

/**
 * Reusable smart-import modal that:
 * 1. Uploads a document
 * 2. Calls a specific extractor endpoint
 * 3. Shows editable extracted fields
 * 4. On confirm calls onApply(extractedData)
 */
export function SmartVisuraImport({ open, onClose, filieraId, onCreated }) {
  const [step, setStep] = useState("upload"); // upload | review
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [model, setModel] = useState("gemini");
  const [data, setData] = useState({});
  const [importoAmmesso, setImportoAmmesso] = useState(0);
  const [contributoAmmesso, setContributoAmmesso] = useState(0);
  const inputRef = useRef();

  const reset = () => {
    setStep("upload"); setFile(null); setData({});
    setImportoAmmesso(0); setContributoAmmesso(0);
  };

  const close = () => { reset(); onClose(); };

  const extract = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("model", model);
    try {
      const { data: r } = await api.post("/ai/extract-visura", fd);
      setData(r.extracted || {});
      setStep("review");
    } catch (e) {
      alert("Errore estrazione: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!data.ragione_sociale) { alert("Ragione sociale mancante"); return; }
    try {
      await api.post("/beneficiari", {
        filiera_id: filieraId,
        ragione_sociale: data.ragione_sociale,
        piva: data.piva, codice_fiscale: data.codice_fiscale,
        indirizzo: data.indirizzo, pec: data.pec, rea: data.rea,
        codice_ateco: data.codice_ateco,
        capitale_sociale: parseFloat(data.capitale_sociale) || 0,
        forma_giuridica: data.forma_giuridica,
        data_costituzione: data.data_costituzione,
        amministratori: Array.isArray(data.amministratori) ? data.amministratori :
          (data.amministratori ? String(data.amministratori).split(",").map((s) => s.trim()) : []),
        email: data.email, telefono: data.telefono,
        importo_ammesso: parseFloat(importoAmmesso) || 0,
        contributo_ammesso: parseFloat(contributoAmmesso) || 0,
      });
      onCreated?.();
      close();
    } catch (e) {
      alert("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Modal open={open} onClose={close} title="Import Beneficiario da Visura Camerale" testId="smart-visura-modal">
      {step === "upload" ? (
        <div className="space-y-4">
          <div className="text-sm text-slate-600">
            Carica una visura camerale (PDF o immagine): l'AI estrarrà automaticamente ragione sociale, P.IVA,
            CF, PEC, REA, ATECO, capitale sociale, amministratori e indirizzo.
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Modello AI">
              <Select value={model} onChange={(e) => setModel(e.target.value)} data-testid="smart-visura-model">
                <option value="gemini">Gemini 3 Flash (consigliato per PDF)</option>
                <option value="openai">GPT-5.4</option>
                <option value="anthropic">Claude Sonnet 4.5</option>
              </Select>
            </Field>
          </div>
          <div className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
            data-testid="smart-visura-dropzone">
            <UploadSimple size={32} className="mx-auto text-slate-400" weight="duotone" />
            <div className="mt-2 text-sm text-slate-700">{file ? file.name : "Trascina o seleziona la visura"}</div>
            <input ref={inputRef} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp"
              onChange={(e) => setFile(e.target.files[0])} data-testid="smart-visura-input" />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Btn variant="secondary" onClick={close}>Annulla</Btn>
            <Btn variant="primary" onClick={extract} disabled={!file || busy} data-testid="smart-visura-extract">
              <Robot size={16} weight="duotone" />
              {busy ? "Analisi AI…" : "Analizza con AI"}
            </Btn>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-700">
            <Sparkle size={16} weight="fill" />
            Dati estratti. Verifica e correggi prima di salvare.
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["ragione_sociale", "Ragione sociale *", 2],
              ["piva", "P.IVA"],
              ["codice_fiscale", "Codice fiscale"],
              ["pec", "PEC"],
              ["rea", "REA"],
              ["codice_ateco", "Codice ATECO"],
              ["forma_giuridica", "Forma giuridica"],
              ["capitale_sociale", "Capitale sociale (€)"],
              ["data_costituzione", "Data costituzione"],
              ["email", "Email"],
              ["telefono", "Telefono"],
              ["indirizzo", "Indirizzo sede legale", 2],
              ["amministratori", "Amministratori (separati da virgola)", 2],
            ].map(([k, label, span]) => (
              <div key={k} className={span === 2 ? "col-span-2" : ""}>
                <Field label={label}>
                  <Input value={Array.isArray(data[k]) ? data[k].join(", ") : (data[k] ?? "")}
                    onChange={(e) => setData({ ...data, [k]: e.target.value })}
                    data-testid={`visura-field-${k}`} />
                </Field>
              </div>
            ))}
            <Field label="Importo ammesso (€)">
              <Input type="number" value={importoAmmesso}
                onChange={(e) => setImportoAmmesso(e.target.value)} data-testid="visura-importo" />
            </Field>
            <Field label="Contributo ammesso (€)">
              <Input type="number" value={contributoAmmesso}
                onChange={(e) => setContributoAmmesso(e.target.value)} data-testid="visura-contributo" />
            </Field>
          </div>
          <div className="flex justify-between gap-2 pt-4 border-t border-slate-200">
            <Btn variant="ghost" onClick={() => setStep("upload")}>← Ricarica documento</Btn>
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={close}>Annulla</Btn>
              <Btn variant="primary" onClick={save} data-testid="visura-save">
                <CheckCircle size={16} weight="bold" /> Crea beneficiario
              </Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function SmartInvestimentiImport({ open, onClose, filiere, beneficiari, onCreated }) {
  const [step, setStep] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [filieraId, setFilieraId] = useState("");
  const [beneficiarioId, setBeneficiarioId] = useState("");
  const [proposed, setProposed] = useState([]);
  const [selected, setSelected] = useState({});
  const inputRef = useRef();

  const reset = () => { setStep("upload"); setFile(null); setProposed([]); setSelected({}); };
  const close = () => { reset(); onClose(); };

  const downloadTemplate = async () => {
    const res = await api.get("/templates/investimenti-template.xlsx", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "template_investimenti.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const extract = async () => {
    if (!file || !filieraId || !beneficiarioId) { alert("Filiera, beneficiario e file richiesti"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("filiera_id", filieraId);
    fd.append("beneficiario_id", beneficiarioId);
    try {
      const { data: r } = await api.post("/ai/extract-investimenti-excel", fd);
      setProposed(r.proposed);
      const sel = {}; r.proposed.forEach((_, i) => sel[i] = true);
      setSelected(sel);
      setStep("review");
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const save = async () => {
    const items = proposed.filter((_, i) => selected[i]);
    try {
      await api.post("/ai/apply-investimenti-bulk", { items });
      onCreated?.();
      close();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Modal open={open} onClose={close} title="Import Investimenti da Excel" testId="smart-investimenti-modal">
      {step === "upload" ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
            <Sparkle size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
            <div>
              Scarica il template Excel, compilalo per il beneficiario, poi caricalo. Colonne attese:
              <span className="mono text-[11px] block mt-1">Tabella investimento · Categoria · Titolo Investimento · Importo approvato · Contributo · Finanziamento agevolato · IVA · Mezzi Propri · TAG · Via · Civico · Comune · Provincia · Foglio · Particella/e</span>
            </div>
          </div>
          <Btn variant="secondary" onClick={downloadTemplate} data-testid="download-template-btn">
            <UploadSimple size={16} weight="bold" /> Scarica template Excel
          </Btn>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Filiera *">
              <Select value={filieraId} onChange={(e) => { setFilieraId(e.target.value); setBeneficiarioId(""); }} data-testid="inv-imp-filiera">
                <option value="">--</option>
                {filiere.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </Select>
            </Field>
            <Field label="Beneficiario *">
              <Select value={beneficiarioId} onChange={(e) => setBeneficiarioId(e.target.value)} data-testid="inv-imp-beneficiario">
                <option value="">--</option>
                {beneficiari.filter((b) => !filieraId || b.filiera_id === filieraId).map((b) => <option key={b.id} value={b.id}>{b.ragione_sociale}</option>)}
              </Select>
            </Field>
          </div>
          <div className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
            data-testid="inv-imp-dropzone">
            <UploadSimple size={32} className="mx-auto text-slate-400" weight="duotone" />
            <div className="mt-2 text-sm">{file ? file.name : "Trascina o seleziona file Excel/CSV"}</div>
            <input ref={inputRef} type="file" hidden accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files[0])} data-testid="inv-imp-input" />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Btn variant="secondary" onClick={close}>Annulla</Btn>
            <Btn variant="primary" onClick={extract} disabled={busy || !file} data-testid="inv-imp-extract">
              <Robot size={16} weight="duotone" /> {busy ? "Analisi…" : "Analizza e importa"}
            </Btn>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-slate-700">
            <b>{proposed.length}</b> investimenti trovati. Verifica righe e conferma.
            <span className="text-slate-500"> · {Object.values(selected).filter(Boolean).length} selezionati</span>
          </div>
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-md">
            <table className="data-table">
              <thead><tr><th></th><th>Tab</th><th>Cat</th><th>Titolo</th><th>Totale</th><th>Contributo</th><th>Warning</th></tr></thead>
              <tbody>
                {proposed.map((p, i) => (
                  <tr key={i} className={selected[i] ? "" : "opacity-50"}>
                    <td><input type="checkbox" checked={!!selected[i]} onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })} data-testid={`inv-imp-sel-${i}`} /></td>
                    <td className="font-medium">{p.tabella}</td>
                    <td>{p.categoria}</td>
                    <td className="max-w-xs truncate">{p.descrizione}</td>
                    <td>€ {p.totale?.toFixed(2)}</td>
                    <td>€ {p.contributo?.toFixed(2)}</td>
                    <td>{p._warnings?.length ? <Badge tone="amber">{p._warnings.length}</Badge> : <Badge tone="green">OK</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between pt-4 border-t border-slate-200">
            <Btn variant="ghost" onClick={() => setStep("upload")}>← Ricarica</Btn>
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={close}>Annulla</Btn>
              <Btn variant="primary" onClick={save} data-testid="inv-imp-save">
                <CheckCircle size={16} weight="bold" /> Crea {Object.values(selected).filter(Boolean).length} investimenti
              </Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function SmartDocumentoImport({ open, onClose, docType, filiere, beneficiari, fornitori, onCreated }) {
  // Upload document (contratto/fattura/bonifico), extract, propose links, save
  const [step, setStep] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [filieraId, setFilieraId] = useState("");
  const [beneficiarioId, setBeneficiarioId] = useState("");
  const [model, setModel] = useState("gemini");
  const [data, setData] = useState({});
  const [suggestions, setSuggestions] = useState({ candidates: [] });
  const [parentId, setParentId] = useState("");
  const [investId, setInvestId] = useState("");
  const [investimenti, setInvestimenti] = useState([]);
  const [fatture, setFatture] = useState([]);
  const inputRef = useRef();

  const reset = () => {
    setStep("upload"); setFile(null); setData({}); setSuggestions({ candidates: [] });
    setParentId(""); setInvestId("");
  };
  const close = () => { reset(); onClose(); };

  const typeLabel = { contratto: "Contratto", preventivo: "Preventivo", oda: "Ordine d'Acquisto",
    ddt: "DDT", offerta: "Offerta", lettera_incarico: "Lettera di incarico",
    fattura: "Fattura", bonifico: "Bonifico" }[docType] || docType;

  const parentField = docType === "fattura" ? "Contratto/Preventivo/OdA collegato" :
                      docType === "bonifico" ? "Fattura collegata" :
                      docType === "contratto" || docType === "preventivo" || docType === "oda" ? "Investimento collegato" : null;

  const extract = async () => {
    if (!file || !filieraId) { alert("Filiera e file richiesti"); return; }
    setBusy(true);
    // 1. Upload file with metadata
    const fd = new FormData();
    fd.append("file", file);
    fd.append("filiera_id", filieraId);
    if (beneficiarioId) fd.append("beneficiario_id", beneficiarioId);
    try {
      const up = await api.post("/files/upload", fd);
      const fileId = up.data.file.id;
      // 2. Analyze
      const an = await api.post("/ai/analyze", { file_id: fileId, model });
      const extracted = an.data.analysis || {};
      setData({ ...extracted, _file_id: fileId });
      // 3. Suggest links
      const sug = await api.post("/ai/link-suggestions", {
        doc_type: docType, filiera_id: filieraId, beneficiario_id: beneficiarioId, extracted,
      });
      setSuggestions(sug.data);
      if (sug.data.beneficiario_id && !beneficiarioId) setBeneficiarioId(sug.data.beneficiario_id);
      if (sug.data.candidates?.[0]?.confidence >= 60) {
        const top = sug.data.candidates[0];
        if (docType === "fattura") { setParentId(top.target_id); if (top.investimento_id) setInvestId(top.investimento_id); }
        else if (docType === "bonifico") setParentId(top.target_id);
        else setInvestId(top.target_id);
      }
      // Load ancillary lists
      const [inv, ft] = await Promise.all([
        api.get("/investimenti", { params: beneficiarioId ? { beneficiario_id: beneficiarioId } : {} }),
        docType === "bonifico" ? api.get("/documenti") : Promise.resolve({ data: [] }),
      ]);
      setInvestimenti(inv.data);
      setFatture(ft.data.filter((d) => d.tipo === "fattura"));
      setStep("review");
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    try {
      if (docType === "bonifico") {
        await api.post("/bonifici", {
          filiera_id: filieraId, beneficiario_id: beneficiarioId,
          data: data.data || new Date().toISOString().slice(0, 10),
          importo: parseFloat(data.importo || data.totale) || 0,
          cro_trn: data.cro_trn, causale: data.oggetto,
          fatture_ids: parentId ? [parentId] : [],
        });
      } else {
        // Try to find/create supplier
        let fornitoreId = data.fornitore_id;
        if (!fornitoreId && data.fornitore_ragione_sociale) {
          const existing = fornitori.find((f) => f.ragione_sociale === data.fornitore_ragione_sociale);
          if (existing) fornitoreId = existing.id;
          else {
            const nf = await api.post("/fornitori", {
              ragione_sociale: data.fornitore_ragione_sociale,
              piva: data.fornitore_piva, codice_fiscale: data.fornitore_cf,
            });
            fornitoreId = nf.data.id;
          }
        }
        await api.post("/documenti", {
          filiera_id: filieraId, beneficiario_id: beneficiarioId,
          investimento_id: investId, fornitore_id: fornitoreId,
          tipo: docType, numero: data.numero, data: data.data,
          oggetto: data.oggetto,
          imponibile: parseFloat(data.imponibile) || 0,
          iva: parseFloat(data.iva) || 0,
          totale: parseFloat(data.totale) || 0,
          cup: data.cup, cig: data.cig,
          dicitura_pnrr: data.dicitura_pnrr,
          modalita_pagamento: data.modalita_pagamento,
        });
      }
      onCreated?.();
      close();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Modal open={open} onClose={close} title={`Import ${typeLabel} da documento`} testId="smart-doc-modal">
      {step === "upload" ? (
        <div className="space-y-4">
          <div className="text-sm text-slate-600">
            Carica il documento ({typeLabel}): l'AI estrae automaticamente i dati e propone i collegamenti alla catena
            {docType === "fattura" && " Contratto → Fattura"}
            {docType === "bonifico" && " Fattura → Bonifico"}
            {["contratto", "preventivo", "oda"].includes(docType) && " Investimento → Contratto/Preventivo/OdA"}.
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Filiera *">
              <Select value={filieraId} onChange={(e) => setFilieraId(e.target.value)} data-testid="doc-imp-filiera">
                <option value="">--</option>
                {filiere.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </Select>
            </Field>
            <Field label="Beneficiario (o AI infer)">
              <Select value={beneficiarioId} onChange={(e) => setBeneficiarioId(e.target.value)} data-testid="doc-imp-beneficiario">
                <option value="">-- inferisci da documento --</option>
                {beneficiari.filter((b) => !filieraId || b.filiera_id === filieraId).map((b) => <option key={b.id} value={b.id}>{b.ragione_sociale}</option>)}
              </Select>
            </Field>
            <Field label="Modello AI">
              <Select value={model} onChange={(e) => setModel(e.target.value)}>
                <option value="gemini">Gemini 3 Flash</option>
                <option value="openai">GPT-5.4</option>
                <option value="anthropic">Claude Sonnet 4.5</option>
              </Select>
            </Field>
          </div>
          <div className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
            data-testid="doc-imp-dropzone">
            <UploadSimple size={32} className="mx-auto text-slate-400" weight="duotone" />
            <div className="mt-2 text-sm">{file ? file.name : "Trascina o seleziona documento"}</div>
            <input ref={inputRef} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.docx"
              onChange={(e) => setFile(e.target.files[0])} data-testid="doc-imp-input" />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Btn variant="secondary" onClick={close}>Annulla</Btn>
            <Btn variant="primary" onClick={extract} disabled={busy || !file} data-testid="doc-imp-extract">
              <Robot size={16} weight="duotone" /> {busy ? "Analisi…" : "Analizza + suggerisci link"}
            </Btn>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-700 flex items-center gap-2">
            <Sparkle size={16} weight="fill" /> Dati estratti. Rivedi campi e collegamenti.
          </div>

          {/* Link suggestions */}
          {suggestions.candidates.length > 0 && (
            <div className="border border-blue-200 bg-blue-50 rounded-md p-3">
              <div className="eyebrow mb-2">Collegamenti proposti dall'AI</div>
              <div className="space-y-2">
                {suggestions.candidates.map((c) => {
                  const isSelected = docType === "fattura" || docType === "bonifico" ? c.target_id === parentId : c.target_id === investId;
                  return (
                    <button key={c.target_id} type="button"
                      onClick={() => {
                        if (docType === "fattura") { setParentId(c.target_id); if (c.investimento_id) setInvestId(c.investimento_id); }
                        else if (docType === "bonifico") setParentId(c.target_id);
                        else setInvestId(c.target_id);
                      }}
                      className={`w-full text-left p-2.5 rounded border transition ${isSelected ? "border-blue-600 bg-white ring-2 ring-blue-200" : "border-transparent hover:border-blue-300 hover:bg-white"}`}
                      data-testid={`link-suggest-${c.target_id}`}>
                      <div className="flex items-start justify-between">
                        <div className="text-sm font-medium text-slate-900">{c.label}</div>
                        <Badge tone={c.confidence >= 70 ? "green" : c.confidence >= 40 ? "amber" : "slate"}>{c.confidence}%</Badge>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{c.reasons.join(" · ")}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {suggestions.candidates.length === 0 && parentField && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-700">
              <Warning size={16} weight="fill" /> Nessun collegamento automatico trovato — seleziona manualmente
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {docType !== "bonifico" && (
              <>
                <Field label="Numero"><Input value={data.numero || ""} onChange={(e) => setData({ ...data, numero: e.target.value })} data-testid="doc-review-numero" /></Field>
                <Field label="Data"><Input type="date" value={data.data || ""} onChange={(e) => setData({ ...data, data: e.target.value })} data-testid="doc-review-data" /></Field>
                <Field label="Fornitore ragione sociale"><Input value={data.fornitore_ragione_sociale || ""} onChange={(e) => setData({ ...data, fornitore_ragione_sociale: e.target.value })} /></Field>
                <Field label="Fornitore P.IVA"><Input value={data.fornitore_piva || ""} onChange={(e) => setData({ ...data, fornitore_piva: e.target.value })} /></Field>
                <div className="col-span-2"><Field label="Oggetto"><Input value={data.oggetto || ""} onChange={(e) => setData({ ...data, oggetto: e.target.value })} /></Field></div>
                <Field label="Imponibile (€)"><Input type="number" value={data.imponibile || ""} onChange={(e) => setData({ ...data, imponibile: e.target.value })} /></Field>
                <Field label="IVA (€)"><Input type="number" value={data.iva || ""} onChange={(e) => setData({ ...data, iva: e.target.value })} /></Field>
                <Field label="Totale (€)"><Input type="number" value={data.totale || ""} onChange={(e) => setData({ ...data, totale: e.target.value })} data-testid="doc-review-totale" /></Field>
                <Field label="CUP"><Input value={data.cup || ""} onChange={(e) => setData({ ...data, cup: e.target.value })} /></Field>
                <Field label="CIG"><Input value={data.cig || ""} onChange={(e) => setData({ ...data, cig: e.target.value })} /></Field>
                <Field label="Dicitura PNRR"><Input value={data.dicitura_pnrr || ""} onChange={(e) => setData({ ...data, dicitura_pnrr: e.target.value })} /></Field>
              </>
            )}
            {docType === "bonifico" && (
              <>
                <Field label="Data bonifico"><Input type="date" value={data.data || ""} onChange={(e) => setData({ ...data, data: e.target.value })} /></Field>
                <Field label="Importo (€)"><Input type="number" value={data.importo || data.totale || ""} onChange={(e) => setData({ ...data, importo: e.target.value })} /></Field>
                <Field label="CRO/TRN"><Input value={data.cro_trn || ""} onChange={(e) => setData({ ...data, cro_trn: e.target.value })} /></Field>
                <div className="col-span-2"><Field label="Causale"><Input value={data.oggetto || ""} onChange={(e) => setData({ ...data, oggetto: e.target.value })} /></Field></div>
              </>
            )}
          </div>

          <div className="flex justify-between pt-4 border-t border-slate-200">
            <Btn variant="ghost" onClick={() => setStep("upload")}>← Ricarica</Btn>
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={close}>Annulla</Btn>
              <Btn variant="primary" onClick={save} data-testid="doc-review-save">
                <CheckCircle size={16} weight="bold" /> Salva {typeLabel}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
